'use client';

import type { Voice } from 'ml-text-service-client';
import { JSX, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Text } from '@videoblocks/design-system';

import { ContributorVoiceList } from '../_components/ContributorVoiceList';
import CreditConfirmationModal from '../_components/CreditConfirmationModal';
import GeneratedVoicePlayer from '../_components/GeneratedVoicePlayer';
import TextInput from '../_components/TextInput';
import VoiceList from '../_components/VoiceList';
import { formatDisplayText } from '../_components/utils';
import { useVoiceoverContext } from '../_context/VoiceoverContext';
import { TAB_TO_EVENT_TYPE, Tabs, useVoiceoverTabContext } from '../_context/VoiceoverTabContext';

const TextToSpeech = (): JSX.Element => {
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const { voices, contributorVoices, user, accountCode } = useVoiceoverContext();

    const { updateState, textToSpeech: tabState, playingVoiceId, setPlayingVoiceId } = useVoiceoverTabContext();
    const {
        script,
        calculatedCost,
        generatedAudioUrl,
        generatedAudioHasPlayed,
        generatedAudioVoiceId,
        selectedVoice,
        voiceoverParams: { speed },
        aiContentId,
        lastAiContentId,
    } = tabState;
    // Find generated voice in either AI voices or contributor voices
    const generatedAiVoice = voices.find((v) => v.voice_id === generatedAudioVoiceId);
    const generatedContributorVoice = contributorVoices.find((v) => v.voice_id === generatedAudioVoiceId);
    const generatedAudioVoice = generatedAiVoice ?? generatedContributorVoice;
    const setSelectedVoice = (voiceId: Voice['voice_id']) => updateState({ selectedVoice: voiceId });

    return (
        <>
            <CreditConfirmationModal
                isOpen={confirmModalOpen}
                setIsOpen={setConfirmModalOpen}
                creditCost={calculatedCost}
                buttonText="Generate Voiceover"
                generateRequestBody={() =>
                    JSON.stringify({
                        text: script,
                        voiceId: selectedVoice,
                        speed: (speed as number) / 100,
                        userId: user!.id,
                        accountCode: accountCode,
                    })
                }
            />
            <TextInput
                speed={speed as number}
                script={script ?? ''}
                onSubmit={() => setConfirmModalOpen(true)}
                buttonText="Generate Voiceover"
                playingVoiceId={playingVoiceId}
                voiceId={selectedVoice}
                calculatedCost={calculatedCost}
                showVoicePreview
                placeholder="Enter your script, select a voice, and bring your voiceover to life..."
            />
            {generatedAudioUrl && generatedAudioVoice && (
                <GeneratedVoicePlayer
                    script={script}
                    audioUrl={generatedAudioUrl}
                    hasPlayed={generatedAudioHasPlayed}
                    setHasPlayed={() => updateState({ generatedAudioHasPlayed: true })}
                    title={generatedAudioVoice.name}
                    subtitle={formatDisplayText(generatedAudioVoice.descriptive)}
                    aiContentId={aiContentId}
                    voiceId={generatedAudioVoiceId}
                    aiContentVoiceoverType={TAB_TO_EVENT_TYPE[Tabs.TextToSpeech]}
                    lastAiContentId={lastAiContentId}
                />
            )}
            <ContributorVoiceList
                selectedVoice={selectedVoice}
                setSelectedVoice={setSelectedVoice}
                playingVoiceId={playingVoiceId}
                setPlayingVoiceId={setPlayingVoiceId}
            />
            <div className={twMerge(!generatedAudioUrl && 'mt-20')}>
                <VoiceList
                    setSelectedVoice={setSelectedVoice}
                    selectedVoice={selectedVoice}
                    playingVoiceId={playingVoiceId}
                    setPlayingVoiceId={setPlayingVoiceId}
                />
            </div>
        </>
    );
};

export default TextToSpeech;
